import { getToken } from './auth';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const padded = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
function bufToB64Url(b: ArrayBuffer | null): string {
  if (!b) return '';
  const bytes = new Uint8Array(b);
  let s = '';
  for (const x of bytes) s += String.fromCharCode(x);
  return btoa(s).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

export type PushState = 'unsupported' | 'denied' | 'granted' | 'default' | 'no-vapid' | 'no-auth';

export async function pushState(): Promise<PushState> {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (!getToken()) return 'no-auth';
  return Notification.permission as PushState;
}

export async function publicPushState(): Promise<PushState> {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  return Notification.permission as PushState;
}

export async function ensureSubscribed(): Promise<{ ok: boolean; reason?: string }> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'Push not supported on this browser/OS.' };
  }
  const token = getToken();
  if (!token) return { ok: false, reason: 'Sign in first.' };

  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;

  if (Notification.permission === 'default') {
    const p = await Notification.requestPermission();
    if (p !== 'granted') return { ok: false, reason: 'Permission not granted.' };
  } else if (Notification.permission !== 'granted') {
    return { ok: false, reason: 'Notifications blocked. Enable them in browser/system settings.' };
  }

  const vapid = await fetch(`${API_BASE}/api/push/vapid-public`).then((r) => r.json()) as { key: string | null };
  if (!vapid.key) return { ok: false, reason: 'Push not configured on server.' };

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid.key).buffer as ArrayBuffer,
    });
  }

  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  const body = {
    endpoint: json.endpoint ?? sub.endpoint,
    keys: {
      p256dh: json.keys?.p256dh ?? bufToB64Url(sub.getKey('p256dh')),
      auth: json.keys?.auth ?? bufToB64Url(sub.getKey('auth')),
    },
    user_agent: navigator.userAgent,
  };

  const res = await fetch(`${API_BASE}/api/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) return { ok: false, reason: 'Server rejected subscription.' };
  return { ok: true };
}

export async function ensureSubscribedPublic(): Promise<{ ok: boolean; reason?: string }> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'Push not supported on this browser/OS.' };
  }
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;

  if (Notification.permission === 'default') {
    const p = await Notification.requestPermission();
    if (p !== 'granted') return { ok: false, reason: 'Permission not granted.' };
  } else if (Notification.permission !== 'granted') {
    return { ok: false, reason: 'Notifications blocked. Enable them in browser/system settings.' };
  }

  const vapid = await fetch(`${API_BASE}/api/push/vapid-public`).then((r) => r.json()) as { key: string | null };
  if (!vapid.key) return { ok: false, reason: 'Push not configured on server.' };

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid.key).buffer as ArrayBuffer,
    });
  }
  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  const body = {
    endpoint: json.endpoint ?? sub.endpoint,
    keys: {
      p256dh: json.keys?.p256dh ?? bufToB64Url(sub.getKey('p256dh')),
      auth: json.keys?.auth ?? bufToB64Url(sub.getKey('auth')),
    },
    user_agent: navigator.userAgent,
  };
  const res = await fetch(`${API_BASE}/api/push/subscribe-public`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) return { ok: false, reason: 'Server rejected subscription.' };
  return { ok: true };
}

export async function sendTestPush(): Promise<{ delivered: number }> {
  const token = getToken();
  if (!token) throw new Error('Sign in first.');
  const res = await fetch(`${API_BASE}/api/push/test`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
  return res.json() as Promise<{ delivered: number }>;
}
